import React, { useState, useEffect } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import AppBreadcrumbs from 'components/molecules/AppBreadcrumbs';
import * as AppPics from 'assets/images';
import '../../../../assets/css/reorder_custom.css';
import useReorderEpisodeList from 'hooks/API/GET/useReorderEpisodeList';
import useReorderEpisode from 'hooks/API/POST/useReorderEpisode';
import { notifier } from 'components/Toaster';
import { useQueryClient } from 'react-query';
import { ReactSortable } from 'react-sortablejs';
import { AppButton } from 'components';

function ReorderEpisode() {
  const history = useHistory();
  const { podcastId } = useParams(); // Get podcastId from URL params
  const queryClient = useQueryClient();

  const [episodes, setEpisodes] = useState([]);
  const [previousOrder, setPreviousOrder] = useState([]);

  const {
    data: episodeData,
    isLoading,
    refetch,
  } = useReorderEpisodeList(podcastId, {
    enabled: true,
    config: { params: {} },
    queryKey: ['podcast-episode-reorder', podcastId],
  });

  const { mutate: reorderEpisodes, isLoading: isSaving } = useReorderEpisode(podcastId);

  // Handle initial data loading and refetching
  useEffect(() => {
    if (episodeData && Array.isArray(episodeData)) {
      const sortedEpisodes = [...episodeData].sort(
        (a, b) => (a.sortPosition || 0) - (b.sortPosition || 0)
      );
      setEpisodes(sortedEpisodes);
      setPreviousOrder(sortedEpisodes.map((episode) => episode.id));
    }
  }, [episodeData]);

  const handleEpisodeReOrder = async (newEpisodes) => {
    if (isSaving) return;

    const reorderPayload = newEpisodes.map((episode, index) => ({
      id: episode.id,
      sortPosition: index + 1,
    }));

    reorderEpisodes(reorderPayload, {
      onSuccess: async (res) => {
        console.log('Reorder success:', res);
        notifier.success(res?.data?.message || 'Episodes reordered successfully');
        await queryClient.invalidateQueries(['podcast-episode-reorder', podcastId]);
        await queryClient.invalidateQueries(['podcast-episode', podcastId]);
        await queryClient.refetchQueries(['podcast-episode-reorder', podcastId]);
        // Update previous order after successful save
        setPreviousOrder(newEpisodes.map((episode) => episode.id));
      },
      onError: (err) => {
        console.error('Reorder error:', err);
        notifier.error(err?.response?.data?.message || 'Failed to reorder episodes');
        // Revert to original order on error
        if (episodeData && Array.isArray(episodeData)) {
          const sortedEpisodes = [...episodeData].sort(
            (a, b) => (a.sortPosition || 0) - (b.sortPosition || 0)
          );
          setEpisodes(sortedEpisodes);
        }
      },
    });
  };

  const handleBackNavigation = () => {
    history.push(`/podcast/${podcastId}/episodes`);
  };

  // Handle drag end to ensure state is properly updated
  const handleSortEnd = (newState) => {
    console.log('Sort ended, new state:', newState);
    setEpisodes([...newState]);

    // Check if the order actually changed
    const currentOrder = newState.map((episode) => episode.id);
    const hasOrderChanged = !currentOrder.every((id, index) => id === previousOrder[index]);

    console.log('Checking changes:', {
      hasOrderChanged,
      currentOrder,
      previousOrder,
    });

    // Only call API if order actually changed
    if (hasOrderChanged) {
      handleEpisodeReOrder(newState);
    }
  };

  return (
    <div className="mainWraper coursetabsection">
      <div className="tab">
        <ul className="tab__menu">
          <li className="back">
            <AppButton variantClass="btn-back" onClick={handleBackNavigation}>
              <img src={AppPics.BackSvg} alt="Back" />
            </AppButton>
          </li>
          <li className="active">Reorder Episodes</li>
        </ul>

        <div className="tab__content ___adjust___tab___padding">
          <div className="tab__content-actHead">
            <AppBreadcrumbs
              links={[
                { label: 'Dashboard', to: '/dashboard' },
                { label: 'Podcast Home', to: '/podcast' },
                { label: 'Episodes', to: `/podcast/${podcastId}/episodes` },
              ]}
              current="Reorder Episodes"
            />
          </div>

          <div className="tab__content-actBody">
            <div className="px-5 py-5">
              <div className="tab__contentMenu mb-4">
                <h2 className="prime-head p-0 boldtitle text-nowrap">Reorder Podcast Episodes</h2>
              </div>
              <div className="tab__contentBody mt-4">
                {isLoading ? (
                  <div className="text-center py-5">
                    <p>Loading episodes...</p>
                  </div>
                ) : episodes.length === 0 ? (
                  <div className="text-center py-5">
                    <p className="text-muted">No episodes available to reorder.</p>
                  </div>
                ) : (
                  <ReactSortable
                    list={episodes}
                    setList={handleSortEnd}
                    handle=".episodeGrabHandleID"
                    animation={200}
                    delayOnTouchStart={true}
                    delay={2}
                    disabled={isSaving}
                  >
                    {episodes.map((episode) => (
                      <div className="row mt-3" key={episode.id}>
                        <div className="col-12">
                          <div className="lessonWrap saved lessonWrap--border episodeGrabHandleID episode-item">
                            {/* Status badge */}
                            {episode?.featured && (
                              <span className={`category-badge featured`}>
                                {episode?.featured ? 'Featured' : ' '}
                              </span>
                            )}
                            <div className="lessonWrap__text">
                              <div className="d-flex align-items-center episode-content">
                                <div className="episode-info flex-grow-1">
                                  <span className="lessonWrap__name d-block">{episode.name}</span>
                                  {/* <small className="text-muted">ID: {episode.id}</small> */}
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </ReactSortable>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ReorderEpisode;
